export const truncateLabel = (label, maxLength = 15) =>
  label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;

export const calculateMarginBottom = (data) => {
  return Math.round(
    40 +
    Math.max(
      ...data.map((d) =>
        typeof d.truncated === "string" ? d.truncated.length : 0
      )
    ) * 1.5
  );
};

export const prepareSliderData = (rawData) => {
  const numeric = Object.entries(rawData)
    .map(([val, count]) => {
      const x = parseFloat(val);
      return !isNaN(x) ? { x, count } : null;
    })
    .filter(Boolean);

  const allCounts = numeric.flatMap(({ x, count }) => Array(count).fill(x));
  const mean = allCounts.reduce((a, b) => a + b, 0) / allCounts.length;
  const std = Math.sqrt(
    allCounts.reduce((s, x) => s + (x - mean) ** 2, 0) / allCounts.length
  );

  const [min, max] = [mean - 2 * std, mean + 2 * std];
  return numeric
    .filter(({ x }) => x >= min && x <= max)
    .map(({ x, count }) => ({ x, y: count }))
    .sort((a, b) => a.x - b.x);
};

export const prepareDualCounterData = (rawData) => {
  return Object.entries(rawData)
    .map(([key, count]) => {
      const [x, y] = key.split("-").map(Number);
      return !isNaN(x) && !isNaN(y) ? { x, y, count, z: Math.sqrt(count) * 3 } : null;
    })
    .filter(Boolean);
};

export const prepareMultisliderData = (rawData) => {
  return Object.entries(rawData)
    .map(([label, values]) => {
      if (!Array.isArray(values) || values.length === 0) return null;

      const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
      return {
        label,
        value: parseFloat(avg.toFixed(2)),
        truncated: truncateLabel(label),
      };
    })
    .filter(Boolean);
};


export const prepareDefaultData = (rawData, question) => {
  return Object.entries(rawData)
    .map(([val, count]) => {
      let label = val;
      const idx = parseInt(val);
      if (Array.isArray(question.possible_answers)) {
        label =
          !isNaN(idx) && question.possible_answers[idx]
            ? question.possible_answers[idx]
            : question.possible_answers.includes(val)
              ? val
              : null;
      }
      return label
        ? { answer: label, count, truncated: truncateLabel(label) }
        : null;
    })
    .filter(Boolean);
};

export const prepareSliderDataGroupedByDecile = (rawData) => {
  const values = [];

  Object.entries(rawData).forEach(([val, count]) => {
    const numericVal = parseFloat(val);
    if (!isNaN(numericVal)) {
      for (let i = 0; i < count; i++) {
        values.push(numericVal);
      }
    }
  });

  if (values.length === 0) return [];

  values.sort((a, b) => a - b);

  const decileSize = Math.floor(values.length / 10);
  const deciles = [];

  for (let i = 0; i < 10; i++) {
    const start = i * decileSize;
    const end = i === 9 ? values.length : start + decileSize;
    const slice = values.slice(start, end);

    const label = `${Math.min(...slice).toFixed(1)}–${Math.max(...slice).toFixed(1)}`;
    deciles.push({
      label,
      count: slice.length,
      truncated: label,
    });
  }

  return deciles;
};


export const prepareSliderDataForPieChart = (rawData) => {
  const values = [];

  Object.entries(rawData).forEach(([val, count]) => {
    const numericVal = parseFloat(val);
    if (!isNaN(numericVal)) {
      for (let i = 0; i < count; i++) {
        values.push(numericVal);
      }
    }
  });

  if (values.length === 0) return [];

  values.sort((a, b) => a - b);
  const decileSize = Math.floor(values.length / 10);
  const slices = [];

  for (let i = 0; i < 10; i++) {
    const startIdx = i * decileSize;
    const endIdx = i === 9 ? values.length : (i + 1) * decileSize;
    const sliceValues = values.slice(startIdx, endIdx);

    const min = Math.min(...sliceValues);
    const max = Math.max(...sliceValues);
    const label = `${min.toFixed(1)}–${max.toFixed(1)}`;

    slices.push({
      name: label,
      value: sliceValues.length,
      label,
    });
  }

  return slices;
};

export const prepareSliderDataGroupedByRangeBins = (rawData, min, max) => {
  const allValues = [];

  Object.entries(rawData).forEach(([val, count]) => {
    const numericVal = parseFloat(val);
    if (!isNaN(numericVal)) {
      for (let i = 0; i < count; i++) {
        allValues.push(numericVal);
      }
    }
  });

  const step = (max - min) / 10;
  const bins = new Array(10).fill(0);

  for (const val of allValues) {
    if (val < min || val > max) continue;
    const index = Math.min(Math.floor((val - min) / step), 9);
    bins[index]++;
  }

  return bins.map((count, i) => {
    const from = min + step * i;
    const to = from + step;
    return {
      name: `${from.toFixed(1)}–${to.toFixed(1)}`,
      value: count,
      label: `${from.toFixed(1)}–${to.toFixed(1)}`
    };
  });
};

export const generateShades = (baseHue, slices, variant = "luminosity") => {
  if (variant === "alternate") {
    const colorA = `hsl(${baseHue}, 70%, 60%)`;
    const colorB = `hsl(${baseHue}, 70%, 45%)`;
    return Array.from({ length: slices }, (_, i) => (i % 2 === 0 ? colorA : colorB));
  } else {
    return Array.from({ length: slices }, (_, i) => {
      const lightness = 80 - (i * 30) / slices; // es: da 70% a 50%
      return `hsl(${baseHue}, 70%, ${lightness}%)`;
    });
  }
};

// Funzioni per processare i timestamp
export const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // Se è già un oggetto Date
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // Se è un numero, potrebbe essere:
  // 1. Timestamp Unix (millisecondi)
  // 2. Timestamp Unix (secondi)
  // 3. Serial date di Excel
  if (typeof timestamp === 'number') {
    // Se è un numero molto grande (> 1000000000000), probabilmente è un timestamp Unix in millisecondi
    if (timestamp > 1000000000000) {
      return new Date(timestamp);
    }
    // Se è un numero tra 1 e 1000000, potrebbe essere un serial date di Excel
    // Excel serial date: giorni dal 30 dicembre 1899
    if (timestamp > 1 && timestamp < 1000000) {
      const excelEpoch = new Date(1899, 11, 30); // 30 dicembre 1899
      return new Date(excelEpoch.getTime() + (timestamp - 1) * 86400 * 1000);
    }
    // Altrimenti prova come timestamp Unix in secondi
    if (timestamp > 0) {
      return new Date(timestamp * 1000);
    }
  }
  
  // Se è una stringa, prova vari formati
  if (typeof timestamp === 'string') {
    // Formato: "2/12/2025 14:02:03" (M/D/YYYY HH:mm:ss)
    const format1 = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/;
    const match1 = timestamp.match(format1);
    if (match1) {
      const [, month, day, year, hour, minute, second] = match1;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second));
    }
    
    // Formato: "12/2/25" (M/D/YY)
    const format2 = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/;
    const match2 = timestamp.match(format2);
    if (match2) {
      const [, month, day, year] = match2;
      const fullYear = parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);
      return new Date(fullYear, parseInt(month) - 1, parseInt(day));
    }
    
    // Prova il parsing standard (gestisce anche stringhe ISO da localStorage)
    const parsed = new Date(timestamp);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
    
    // Se è una stringa che sembra un numero seriale di Excel (es. "45754.8086111111")
    const numericMatch = timestamp.match(/^(\d+\.?\d*)$/);
    if (numericMatch) {
      const numValue = parseFloat(numericMatch[1]);
      if (numValue > 1 && numValue < 1000000) {
        const excelEpoch = new Date(1899, 11, 30);
        return new Date(excelEpoch.getTime() + (numValue - 1) * 86400 * 1000);
      }
    }
  }
  
  return null;
};

export const prepareDayOfWeekData = (answerData) => {
  const dayCounts = {
    'Domenica': 0,
    'Lunedì': 0,
    'Martedì': 0,
    'Mercoledì': 0,
    'Giovedì': 0,
    'Venerdì': 0,
    'Sabato': 0
  };
  
  const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
  
  answerData.forEach((entry) => {
    const timestamp = parseTimestamp(entry._timestamp);
    if (timestamp) {
      const dayIndex = timestamp.getDay();
      const dayName = dayNames[dayIndex];
      dayCounts[dayName]++;
    }
  });
  
  return dayNames.map(dayName => ({
    day: dayName,
    count: dayCounts[dayName],
    truncated: dayName
  }));
};

export const prepareHourOfDayData = (answerData) => {
  const hourCounts = new Array(24).fill(0);
  
  answerData.forEach((entry) => {
    const timestamp = parseTimestamp(entry._timestamp);
    if (timestamp) {
      const hour = timestamp.getHours();
      hourCounts[hour]++;
    }
  });
  
  return hourCounts.map((count, hour) => ({
    hour: hour,
    hourLabel: `${hour.toString().padStart(2, '0')}:00`,
    count: count,
    truncated: `${hour.toString().padStart(2, '0')}:00`
  }));
};